import React, { useRef, useState, useMemo } from "react";
import {
    ArrowDownTrayIcon,
    ListBulletIcon,
    PencilIcon,
    CheckIcon,
    XMarkIcon,
    ChevronRightIcon,
} from "@heroicons/react/24/outline";
import { GithubPicker } from "react-color";
import { Menu } from "@headlessui/react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import htmlDocx from "html-docx-js/dist/html-docx";
import { useTranslation } from "react-i18next";

// HEX to RGB helper
function hexToRgb(hex) {
    hex = hex.replace(/^#/, "");
    if (hex.length === 3) {
        hex = hex
            .split("")
            .map((x) => x + x)
            .join("");
    }
    const num = parseInt(hex, 16);
    return [num >> 16, (num >> 8) & 255, num & 255];
}

const customColors = [
    "#ddad9e", "#f8e0c5", "#fcd39e", "#eab976", "#dec487", "#e6dcab", "#f1efb9", "#e2f1b5",
    "#bae295", "#aeea84", "#bbe4ae", "#aceea4", "#8cdd85", "#85eca4", "#bdf3d7", "#87fdd1",
    "#6fdcb4", "#a2dcf1", "#8cd2ef", "#aad2ec", "#bedcf0", "#72b0db", "#9bbbfc", "#a4a3dd",
    "#a8a1e9", "#c7bdf2", "#b29fdf", "#d789fe", "#e5a3f6", "#f4bff8", "#e895b1", "#f597a4",
    "#ffffff", "#000000"
];

// Editable header for each column/row label
const EditableHeader = ({ initialText, onSave }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [text, setText] = useState(initialText);
    const [prevText, setPrevText] = useState(initialText);

    React.useEffect(() => {
        setText(initialText);
    }, [initialText]);

    const handleEdit = () => {
        setPrevText(text);
        setIsEditing(true);
    };

    const handleSave = () => {
        onSave(text);
        setIsEditing(false);
    };

    const handleCancel = () => {
        setText(prevText);
        setIsEditing(false);
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter") {
            handleSave();
        } else if (e.key === "Escape") {
            handleCancel();
        }
    };

    return (
        <div className="flex items-center justify-center gap-2">
            {isEditing ? (
                <input
                    type="text"
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    onBlur={handleSave}
                    onKeyDown={handleKeyDown}
                    className="border rounded px-2 py-1 text-sm w-full"
                    autoFocus
                />
            ) : (
                <>
                    <span>{text}</span>
                    <button
                        onClick={handleEdit}
                        className="p-1 hover:bg-gray-100 rounded transition-colors edit-button"
                    >
                        <PencilIcon className="w-4 h-4 text-gray-500" />
                    </button>
                </>
            )}
        </div>
    );
};

const ColorPickerSection = ({ label, color, onChange }) => {
    const [showPicker, setShowPicker] = useState(false);

    return (
        <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">{label}</label>
            <div className="relative">
                <button
                    className="w-full h-8 rounded border flex items-center px-2 hover:bg-gray-50"
                    onClick={() => setShowPicker(!showPicker)}
                >
                    <div
                        className="w-6 h-6 rounded mr-2 border"
                        style={{ backgroundColor: color }}
                    />
                    <span className="text-sm">{color}</span>
                </button>
                {showPicker && (
                    <div className="absolute z-10 mt-1">
                        <div
                            className="fixed inset-0"
                            onClick={() => setShowPicker(false)}
                        />
                        <GithubPicker
                            color={color}
                            colors={customColors}
                            onChange={(color) => {
                                onChange(color.hex);
                                setShowPicker(false);
                            }}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

const ExportWrapper = ({
    children,
    title,
    visibleItems,
    visibleItemsState,
    setVisibleItemsState,
    headerBgColor = "#ffffff",
    headerTextColor = "#000000",
    files = [],
    rows = [],
    presenceMatrix = {},
    showCount = false,
    fileHeaders = {},
    codeHeaders = {},
    mainCodeHeader = "Code",
}) => {
    const wrapperRef = useRef(null);
    const { t } = useTranslation();

    // PNG Export (just takes screenshot of DOM)
    const downloadAsImage = async () => {
        try {
            const element = wrapperRef.current;
            const editButtons = element.querySelectorAll(".edit-button");
            editButtons.forEach((button) => {
                button.style.display = "none";
            });

            const style = document.createElement("style");
            style.innerHTML = `
                td[rowspan], td, th {
                    border: 1px solid #e5e7eb !important;
                }
            `;
            document.head.appendChild(style);

            const canvas = await html2canvas(element);

            document.head.removeChild(style);
            editButtons.forEach((button) => {
                button.style.display = "";
            });

            const image = canvas.toDataURL("image/png");
            const link = document.createElement("a");
            link.href = image;
            link.download = `${title}.png`;
            link.click();
        } catch (error) {
            console.error("Error generating image:", error);
        }
    };

    // PDF Export: custom colors for present/absent cells
    const exportAsPDF = async () => {
        const bgRgb = hexToRgb(headerBgColor);
        const textRgb = hexToRgb(headerTextColor);
        const doc = new jsPDF({
            orientation: "landscape",
            unit: "pt",
            format: "a4",
        });

        // Prepare header row
        const columns = [
            { content: mainCodeHeader, styles: { halign: "left", fillColor: bgRgb, textColor: textRgb } },
            ...files.map(file => ({
                content: fileHeaders[file.id] || file.name,
                styles: { halign: "left", fillColor: bgRgb, textColor: textRgb },
            })),
        ];

        // Prepare body rows
        const data = rows.map(code => [
            {
                content: codeHeaders[code.id || code.uuid] || code.name,
                styles: {
                    halign: "left",
                    fontStyle: code.parent ? "" : "bold",
                    fillColor: code.parent ? [245, 245, 245] : [255, 255, 255],
                },
            },
            ...files.map(file => {
                const count = presenceMatrix[code.id || code.uuid]?.[file.id] || 0;
                if (showCount) {
                    return {
                        content: count > 0 ? count.toString() : "0",
                        styles: {
                            halign: "center",
                            fillColor: count > 0 ? [200, 255, 200] : [255, 200, 200],
                        },
                    };
                } else {
                    return {
                        content: "",
                        styles: {
                            halign: "center",
                            fillColor: count > 0 ? [76, 175, 80] : [239, 68, 68],
                        },
                    };
                }
            }),
        ]);

        autoTable(doc, {
            head: [columns],
            body: data,
            startY: 30,
            styles: {
                fontSize: 10,
                cellPadding: 4,
                valign: "middle",
            },
            theme: "grid",
            margin: { left: 20, right: 20 },
            tableWidth: "auto",
            didDrawPage: function () {
                doc.setFontSize(16);
                doc.text(
                    title || "Table Export",
                    doc.internal.pageSize.getWidth() / 2,
                    22,
                    { align: "center" }
                );
            },
        });

        doc.save(`${title}.pdf`);
    };

    // Word Export: custom HTML for red/green backgrounds
    const exportAsWord = async () => {
        const thStyle = `
            background:${headerBgColor};color:${headerTextColor};border:1px solid #e5e7eb;padding:8px;font-weight:600;text-align:left;
        `;
        const green = "#4CAF50", red = "#EF4444", lightGreen = "#c8ffc8", lightRed = "#ffc8c8";

        let html = `<style>
            table, th, td { border: 1px solid #e5e7eb; border-collapse: collapse; }
            th, td { padding: 8px; }
        </style>`;
        html += "<table>";
        html += "<thead><tr>";
        html += `<th style="${thStyle}">${mainCodeHeader}</th>`;
        files.forEach(file => {
            html += `<th style="${thStyle}">${fileHeaders[file.id] || file.name}</th>`;
        });
        html += "</tr></thead><tbody>";
        rows.forEach(code => {
            html += "<tr>";
            html += `<td style="font-weight:${code.parent ? "normal" : "bold"};${code.parent ? "background:#f5f5f5;" : ""}">${codeHeaders[code.id || code.uuid] || code.name}</td>`;
            files.forEach(file => {
                const count = presenceMatrix[code.id || code.uuid]?.[file.id] || 0;
                if (showCount) {
                    html += `<td style="background:${count > 0 ? lightGreen : lightRed};text-align:center;">${count > 0 ? count : 0}</td>`;
                } else {
                    html += `<td style="background:${count > 0 ? green : red};text-align:center;"></td>`;
                }
            });
            html += "</tr>";
        });
        html += "</tbody></table>";

        const converted = htmlDocx.asBlob(html);
        const link = document.createElement("a");
        link.href = URL.createObjectURL(converted);
        link.download = `${title}.docx`;
        link.click();
    };

    return (
        <div>
            <div className="flex flex-col lg:flex-row  justify-between items-center">
                <h2 className="text-xl font-semibold whitespace-nowrap ">
                    {title}
                </h2>
                <div className="flex flex-collg:flex-row w-full justify-between lg:justify-end gap-2 py-2 items-center">
                    <button
                        onClick={() => setVisibleItemsState && setVisibleItemsState(v => !v)}
                        className="flex w-full lg:w-fit items-center gap-2 px-3 py-1 bg-black text-white rounded hover:bg-gray-800 transition-colors text-sm"
                    >
                        <ListBulletIcon className="w-4 h-4" />
                        {t ? t("CategoryResults.options") : "Options"}
                    </button>
                    <Menu as="div" className="relative">
                        <Menu.Button className="flex w-full lg:w-fit items-center gap-2 px-3 py-1 bg-black text-white rounded hover:bg-gray-800 transition-colors text-sm">
                            <ArrowDownTrayIcon className="w-4 h-4" />
                            {t ? t("CategoryResults.Export") : "Export"}
                        </Menu.Button>
                        <Menu.Items className="absolute right-0 mt-2 w-48 origin-top-right bg-white border border-gray-200 divide-y divide-gray-100 rounded shadow-lg focus:outline-none z-50">
                            <Menu.Item>
                                {({ active }) => (
                                    <button
                                        onClick={downloadAsImage}
                                        className={`block w-full text-left px-4 py-2 text-sm ${active ? "bg-gray-100" : ""
                                            }`}
                                    >
                                        PNG
                                    </button>
                                )}
                            </Menu.Item>
                            <Menu.Item>
                                {({ active }) => (
                                    <button
                                        onClick={exportAsPDF}
                                        className={`block w-full text-left px-4 py-2 text-sm ${active ? "bg-gray-100" : ""
                                            }`}
                                    >
                                        PDF
                                    </button>
                                )}
                            </Menu.Item>
                            <Menu.Item>
                                {({ active }) => (
                                    <button
                                        onClick={exportAsWord}
                                        className={`block w-full text-left px-4 py-2 text-sm ${active ? "bg-gray-100" : ""
                                            }`}
                                    >
                                        Word
                                    </button>
                                )}
                            </Menu.Item>
                        </Menu.Items>
                    </Menu>
                </div>
            </div>
            {visibleItems && visibleItems}
            <div className="overflow-x-auto border ">
                <div ref={wrapperRef} className="bg-white min-w-[1024px]">
                    {children}
                </div>
            </div>
        </div>
    );
};

export default function CodeFilePresenceMatrix({
    project,
    codes,
    title = "Code Presence Matrix",
    ...exportProps
}) {
    const { t } = useTranslation();
    const [headerBgColor, setHeaderBgColor] = useState("#ffffff");
    const [headerTextColor, setHeaderTextColor] = useState("#000000");
    const [showSubcodes, setShowSubcodes] = useState(true);
    const [showCount, setShowCount] = useState(false);

    // Editable header state
    const [mainCodeHeader, setMainCodeHeader] = useState(t("Code") || "Code");
    const [fileHeaders, setFileHeaders] = useState({});
    const [codeHeaders, setCodeHeaders] = useState({});

    // Extract files/analyses (columns)
    const files = useMemo(() => {
        if (!project?.analysis) return [];
        return project.analysis.map(a => ({
            id: a.id,
            name: a.name || a.filename || a.uuid || a.id,
            uuid: a.uuid,
        }));
    }, [project]);

    // Flatten codes and subcodes into rows
    const getAllRows = (codes, parent = null) => {
        let rows = [];
        codes.forEach(code => {
            // If subcodes are hidden, only show top-level codes
            if (!showSubcodes && code.parent) return;
            rows.push({ ...code, parent });
            // If showing subcodes and they exist, recurse
            if (showSubcodes && Array.isArray(code.children) && code.children.length > 0) {
                rows = rows.concat(getAllRows(code.children, code));
            }
        });
        return rows;
    };

    const rows = useMemo(() => getAllRows(codes), [codes, showSubcodes]);

    // Build presence matrix: code id x file id -> count
    const presenceMatrix = useMemo(() => {
        const matrix = {};
        rows.forEach(code => {
            const counts = {};
            files.forEach(file => {
                counts[file.id] = (code.excerpts || []).filter(
                    excerpt => excerpt.analysis_parent_id === file.id
                ).length;
            });
            matrix[code.id || code.uuid] = counts;
        });
        return matrix;
    }, [rows, files]);

    // Style for table headers
    const headerStyle = {
        backgroundColor: headerBgColor,
        color: headerTextColor,
        verticalAlign: "middle",
    };

    // For visibleItems/options panel (column toggles/colors)
    const [visibleItemsState, setVisibleItemsState] = useState(false);
    const [visibleItems, setVisibleItems] = useState(null);

    React.useEffect(() => {
        if (visibleItemsState === true) {
            setVisibleItems(
                <div className="grid lg:grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg my-2">
                    <div className="flex flex-col gap-2">
                        <label className="flex items-center gap-2">
                            <input
                                type="checkbox"
                                checked={showSubcodes}
                                onChange={() => setShowSubcodes(v => !v)}
                                className="rounded border-gray-300 text-black focus:ring-black"
                            />
                            {t("CategoryResults.subcategories")}
                        </label>
                        <label className="flex items-center gap-2">
                            <input
                                type="checkbox"
                                checked={showCount}
                                onChange={() => setShowCount(v => !v)}
                                className="rounded border-gray-300 text-black focus:ring-black"

                            />
                            {t("CategoryResults.categoryExcerptCount")}
                        </label>
                    </div>
                    <div className="flex flex-col gap-4">
                        <ColorPickerSection
                            label={t("CategoryResults.headerBackgroundColor") || "Header Background"}
                            color={headerBgColor}
                            onChange={setHeaderBgColor}
                        />
                        <ColorPickerSection
                            label={t("CategoryResults.headerTextColor") || "Header Text Color"}
                            color={headerTextColor}
                            onChange={setHeaderTextColor}
                        />
                    </div>
                </div>
            );
        } else {
            setVisibleItems(null);
        }
    }, [visibleItemsState, showSubcodes, showCount, headerBgColor, headerTextColor, t]);

    // Editable header update handlers
    const handleFileHeaderUpdate = (id, newText) => {
        setFileHeaders(prev => ({ ...prev, [id]: newText }));
    };
    const handleCodeHeaderUpdate = (id, newText) => {
        setCodeHeaders(prev => ({ ...prev, [id]: newText }));
    };

    return (
        <div className="space-y-4">
            <div>
                <div className="border rounded-lg border-black shadow p-4">
                    <ExportWrapper
                        title={title}
                        visibleItems={visibleItems}
                        visibleItemsState={visibleItemsState}
                        setVisibleItemsState={setVisibleItemsState}
                        headerBgColor={headerBgColor}
                        headerTextColor={headerTextColor}
                        files={files}
                        rows={rows}
                        presenceMatrix={presenceMatrix}
                        showCount={showCount}
                        fileHeaders={fileHeaders}
                        codeHeaders={codeHeaders}
                        mainCodeHeader={mainCodeHeader}
                    >
                        {/* Table */}
                        <div className="overflow-x-auto  rounded-lg">
                            <table className="w-full border-collapse min-w-[600px]">
                                <thead style={headerStyle}>
                                    <tr>
                                        <th className="p-3 text-left border" style={headerStyle}>
                                            <EditableHeader
                                                initialText={mainCodeHeader}
                                                onSave={setMainCodeHeader}
                                            />
                                        </th>
                                        {files.map(file => (
                                            <th key={file.id} className="p-3 text-center border justify-center" style={headerStyle}>
                                                <EditableHeader
                                                    initialText={fileHeaders[file.id] || file.name}
                                                    onSave={text => handleFileHeaderUpdate(file.id, text)}
                                                />
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((code) => (
                                        <tr key={code.id || code.uuid} className={code.parent ? "bg-gray-50" : ""}>
                                            {/* Code name, with indent for subcodes */}
                                            <td className="p-3 border text-start" style={{
                                                paddingLeft: code.parent ? "2rem" : "0.75rem",
                                                fontWeight: code.parent ? "normal" : "bold"
                                            }}>
                                                {code.name}
                                            </td>
                                            {files.map(file => {
                                                const count = presenceMatrix[code.id || code.uuid][file.id];
                                                return (
                                                    <td key={file.id} className="p-3 border text-center">
                                                        {showCount ? (
                                                            count > 0
                                                                ? <span className="font-semibold">{count}</span>
                                                                : <span className="text-gray-300">0</span>
                                                        ) : (
                                                            count > 0
                                                                ? <CheckIcon className="w-5 h-5 text-green-500 inline" />
                                                                : <XMarkIcon className="w-5 h-5 text-red-300 inline" />
                                                        )}
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </ExportWrapper>
                </div>
            </div>
        </div>
    );
}
